import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import {
    setEditorContext,
    setIsEmojiPickerOpen,
    setSnackbarMessage,
    setMessage
} from '@common/reducers/ScheduledMessageEditorReducer'
import { Menu, Snackbar, Stack } from '@mui/material'
import { Box } from '@mui/system'
import EmojiPicker, { EmojiClickData } from '@/common/components/lazy-emoji-picker/lazy-emoji-picker'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { usePortalContainer } from '@/common/hooks/use-portal-container'

import CreatePoll from "./create-poll"
import { LinkPreviewBox } from './link-preview-box'
import MessageEditorAttachmentBox from './message-editor-attachment-box'
import MessageEditorDropFilesOverlay from './message-editor-drop-files-overlay'
import MessageEditorFooter from './message-editor-footer'
import LexicalMessageEditor, { LexicalMessageEditorRef } from './lexical/LexicalMessageEditor'
import MessageEditorTemplateHeaderAndTopButtons from './message-editor-template-header-and-top-buttons'
import ReplyToMessageChip from "./reply-to-message-chip"
import TemplatePlaceholderBar from './template-placeholder-bar'
import GoogleTranslateErrorBoundary from '../google-translate-error-boundary/google-translate-error-boundary'

type Props = {
    isPreview?: boolean;
    editorContext: 'scheduleMessage' | 'scheduleCampaign';
    /**
     * FE#384 — stretch the composer to fill the available height (opt-in, used by
     * the responsive tabbed Schedule dialog's Message tab). The editor input then
     * grows to fill all remaining space instead of the fixed 170–400px band, with
     * the toolbar/footer pinned. Default false keeps every other surface unchanged.
     */
    fillHeight?: boolean;
}

// Template reducer removed - using simple decoupled state instead

export default function MessageEditorControlled({
    isPreview = false,
    editorContext,
    fillHeight = false,
}: Props) {
    const dispatch = useAppDispatch()


    const isPoll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isPoll);
    const sendLinkPreview = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.sendLinkPreview);
    const assetType = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.asset?.type);
    const isEmojiPickerOpen = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isEmojiPickerOpen);
    const snackbarMessage = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.snackbarMessage);
    const templateId = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.templateId);
    const message = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.message);
    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);

    const emojiButtonRef = useRef<HTMLButtonElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messageBoxRef = useRef<LexicalMessageEditorRef>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // Get the portal container for Shadow DOM support
    const portalContainer = usePortalContainer();
    
    // Calculate Menu container for Shadow DOM portal support
    const menuContainer = useMemo(() => {
        // Only use HTMLElement, not ShadowRoot (MUI doesn't accept ShadowRoot)
        const containerElement = portalContainer instanceof HTMLElement 
            ? portalContainer 
            : null;
        const isShadowDOM = containerElement && containerElement !== document.body;
        // Return container for Shadow DOM, undefined for normal DOM (MUI will use document.body)
        return isShadowDOM ? containerElement : undefined;
    }, [portalContainer]);

    // Only UI-specific state that doesn't affect data

    // Dropzone for file uploads
    const onDrop = (acceptedFiles: File[]) => {
        if (fileInputRef.current && acceptedFiles.length > 0) {
            // Create a file list and assign it to the input
            const dt = new DataTransfer();
            acceptedFiles.forEach(file => dt.items.add(file));
            fileInputRef.current.files = dt.files;

            // Trigger the change event
            const event = new Event('change', { bubbles: true });
            fileInputRef.current.dispatchEvent(event);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
    });

    // Simple computed values
    const isAttachmentSound = assetType?.includes('audio')

    const onEmojiClick = (emoji: EmojiClickData, event: MouseEvent) => {
        if (messageBoxRef.current) {
            messageBoxRef.current.insertEmojiAtCursor(emoji.emoji);
        }
    };

    const handleEmojiPickerToggle = () => {
        // Store cursor position before opening emoji picker
        if (messageBoxRef.current && !isEmojiPickerOpen) {
            messageBoxRef.current.storeCursorPosition();
        }
        dispatch(setIsEmojiPickerOpen(!isEmojiPickerOpen));
    };

    // Set editorContext in Redux when component initializes
    useEffect(() => {
        dispatch(setEditorContext(editorContext));
    }, [dispatch, editorContext]);

    // Stable onChange so LexicalMessageEditor's internal 300ms debounce is not
    // recreated (and its pending trailing dispatch discarded) on every render.
    const handleMessageChange = useCallback((newMessage: string) => {
        dispatch(setMessage(newMessage));
    }, [dispatch]);

    return (
        <GoogleTranslateErrorBoundary>
            {/* The bt-msg-editor-* classes are prod's FE#385 inert styling hooks
                (kept). FE#384's opt-in `fillHeight` additionally makes this chain a
                flex-fill column so the tabbed dialog's editor claims all height. */}
            <div className="bt-msg-editor-root" style={{ width: '100%', marginBottom: fillHeight ? 0 : '15px', ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' as const } : {}) }} {...getRootProps()} translate="no">
                {/* Dropzone input for drag and drop */}
                <input {...getInputProps()} style={{ display: 'none' }} />

            {isPoll &&
                <CreatePoll />
            }

            {!isPoll &&
                <Stack spacing={2}
                    className="bt-msg-editor-box"
                    sx={{
                        position: 'relative',
                        width: '100%',
                        border: '1px solid var(--tasks-cell-border)',
                        borderRadius: isPreview ? '10px' : '10px 10px 0px 0px',
                        boxSizing: 'border-box',
                        backgroundColor: 'var(--color-surface)',
                        // FE#384 fill mode: the composer card claims the tab's height.
                        ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
                    }}>
                    
                    {/* Drop overlay when dragging files - covers entire editor */}
                    {!isPreview && isDragActive && <MessageEditorDropFilesOverlay />}

                    <ReplyToMessageChip height='83px' />

                    {sendLinkPreview &&
                        <LinkPreviewBox
                            isPreview={isPreview}
                        />
                    }


                    <Box className="bt-msg-editor-body" sx={{ width: '100%', m: '0px !important', ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
                        {!isPreview && !isAttachmentSound &&
                            <>
                                <MessageEditorTemplateHeaderAndTopButtons
                                    emojiButtonRef={emojiButtonRef}
                                    fileInputRef={fileInputRef}
                                    onFormatRequest={(symbol) => messageBoxRef.current?.formatBySymbol(symbol)}
                                    onEmojiPickerToggle={handleEmojiPickerToggle}
                                />
                            </>
                        }

                        <Stack direction="row" className="bt-msg-editor-input-row" style={{ position: 'relative', ...(fillHeight ? { flex: 1, minHeight: 0 } : {}) }}>
                            {!isAttachmentSound &&
                                <LexicalMessageEditor
                                    key={templateId || 'default'}
                                    ref={messageBoxRef}
                                    isPreview={isPreview}
                                    content={message}
                                    onChange={handleMessageChange}
                                    fillHeight={fillHeight}
                                    onFilesSelected={(files) => {
                                        console.log('Files dropped on Lexical editor:', files);
                                    }}
                                />
                            }
                            <MessageEditorAttachmentBox
                                isPreview={isPreview}
                                inputRef={fileInputRef}
                                setSnackbarMessage={(message: string) => dispatch(setSnackbarMessage(message))}
                                onFilesSelected={(files) => {
                                    // Handle files directly without needing to trigger events
                                    console.log('Files dropped on message box:', files);
                                }}
                            />
                        </Stack>

                        <Menu
                            open={isEmojiPickerOpen}
                            anchorEl={emojiButtonRef?.current}
                            onClose={() => dispatch(setIsEmojiPickerOpen(false))}
                            {...(menuContainer ? { 
                                // Set container directly for Shadow DOM portal support
                                container: menuContainer
                            } : {})}
                            disableAutoFocus
                            disableEnforceFocus
                            sx={{
                                zIndex: 21000, // Must be above Radix DialogContent (z-index: 20000)
                                pointerEvents: 'auto', // Ensure Popover root allows pointer events
                                '& .MuiPopover-root': {
                                    pointerEvents: 'auto', // Ensure Popover root allows pointer events
                                },
                                '& .MuiPaper-root': {
                                    zIndex: 21000, // Must be above Radix DialogContent (z-index: 20000)
                                    pointerEvents: 'auto', // Ensure clicks work on the menu
                                },
                                '& .MuiList-root': {
                                    pointerEvents: 'auto', // Ensure clicks work on the list
                                    padding: 0, // Remove default padding that might interfere
                                }
                            }}
                            PaperProps={{
                                sx: {
                                    zIndex: 21000, // Must be above Radix DialogContent (z-index: 20000)
                                    pointerEvents: 'auto', // CRITICAL: Ensure pointer events are enabled so emojis are clickable
                                }
                            }}
                            MenuListProps={{
                                sx: {
                                    pointerEvents: 'auto', // Ensure the menu list receives pointer events
                                    padding: 0, // Remove default padding
                                }
                            }}
                        >
                            <Box sx={{ pointerEvents: 'auto' }}>
                                <EmojiPicker onEmojiClick={onEmojiClick} />
                            </Box>
                        </Menu>

                        {!isPreview && editorContext === 'scheduleCampaign' && !isAttachmentSound &&
                            <TemplatePlaceholderBar
                                messageBoxRef={messageBoxRef}
                            />
                        }
                    </Box>


                    <Snackbar
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center"
                        }}
                        open={!!snackbarMessage}
                        autoHideDuration={3000}
                        onClose={() => { dispatch(setSnackbarMessage(null)) }}
                        ContentProps={{
                            sx: {
                                display: 'block',
                                textAlign: "center",
                                backgroundColor: '#323232 !important',
                                color: 'var(--color-surface) !important',
                                '& .MuiSnackbarContent-message': {
                                    color: 'var(--color-surface) !important',
                                }
                            }
                        }}
                        message={snackbarMessage}
                    />
                </Stack>
            }

            {!isPreview &&
                <MessageEditorFooter />
            }
            </div>
        </GoogleTranslateErrorBoundary>
    );
} 